import React, { useState, useMemo, useCallback, useContext } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { LoginContext } from "../../../../services/api/authentication-api";
import { clearDetailStateFromStorage, getAccordionRowRender, getInitialDetailState, PowerBankerNoDealOnFilterState, PowerCapacity, PowerStafferEmptyState, saveDetailStateToStorage, useMemberGroupToggle } from "../dashboard-pui-banker/power-banker-time-log/power-banker-helper";
import { createColumnMenu, formatLastUpdatedDate, renderlastUpdatedTooltipHeader, renderTooltipHeader } from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../dashboard-util/dashboard-utils";
import "../dashboard-pui-banker/power-banker-time-log/power-banker-time-log.scss";
import { FluentAvatar } from "../../../../components/FluentUI/Fluent-Avatar";
import DashboardPowerMemberTrackerHeader from "./dashboard-pui-member-tracker-header";
import PowerMemberTrackerModal from "./dashboard-pui-member-tracker-modal";
import PropTypes from "prop-types";
import { getAnalystFilteredSortedData, getFlattenedData } from "../../dashboard-energy/dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";

const DashboardPuiMemberTracker = ({
    members = {},
}) => {
    const user = useContext(LoginContext);
    const [memberState, setMemberState] = useState(() => getInitialDetailState(user?.userID, "powerMemberTracker"));
    const [show, setShow] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const { groupState, toggleGroup, setGroupState } = useMemberGroupToggle();
    const flattenedData = useMemo(() =>
        getFlattenedData(members), [members]);
    const filteredData = useMemo(() =>
        getAnalystFilteredSortedData(flattenedData, memberState.filter, memberState.sort), [flattenedData, memberState.filter, memberState.sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const columnMenu = createColumnMenu(flattenedData);
    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty ? [{ isEmptyRow: true, showSearchEmptyState: false, isFilterEmptyState: (memberState.filter?.filters?.length ?? 0) > 0 }] : visibleData;
    const AccordionRowRender =
        useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup, 7, "GroupName", "Banker"), [filteredData, groupState]);
    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, memberState.filter),
        [memberState.filter]
    );

    const onHide = () => {
        setShow(false);
        setSelectedMember(null);
    };
    const onRowClick = (event) => {
        setSelectedMember(event.dataItem);
        setShow(true);
    };
    const resetDataState = () => {
        const resetState = { sort: [], filter: null };
        setMemberState(resetState);
        clearDetailStateFromStorage(user?.userID, "powerMemberTracker");
    };

    return (
        <div className="power-banker-time-log power-staffer-widgets clickable-row dashboard-container-shadow">
            <DashboardPowerMemberTrackerHeader
                members={members}
                resetDataState={resetDataState}
                memberState={memberState}
            />
            <div className="detail-grid-scroll-container" data-testid="energy-grid">
                <Grid
                    data={finalGridData}
                    dataItemKey={"ProjectId"}
                    sortable={true}
                    sort={memberState.sort}
                    filter={memberState.filter}
                    onSortChange={(e) => {
                        setMemberState({ ...memberState, sort: e.sort })
                        saveDetailStateToStorage(user.userID, { ...memberState, sort: e.sort }, "", "powerMemberTracker");
                        setGroupState({ ...groupState });
                    }}
                    onFilterChange={(e) => {
                        saveDetailStateToStorage(user.userID, { ...memberState, filter: e.filter }, "", "powerMemberTracker");
                        setMemberState({ ...memberState, filter: e.filter })
                    }}
                    onRowClick={onRowClick}
                    resizable={true}
                    rowRender={AccordionRowRender}
                >

                    <Column
                        field="Banker"
                        title={renderTooltipHeader("Analyst/Associate")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                        cell={(props) => (
                            members?.length > 0
                                ? props.dataItem.isFilterEmptyState
                                    ? <td colSpan={7} className=" k-table-td ">
                                        <PowerBankerNoDealOnFilterState resetDataState={resetDataState} />
                                    </td>
                                    : <td className="background-white k-table-td ">
                                        <div className="dashboard-username" style={{ pointerEvents: 'none' }}>
                                            <FluentAvatar size={28} name={props.dataItem.Banker} className="task-member-user mr-2" />
                                            <NameAndNoDealsBadge onRowClick={() => onRowClick(props)} name={props.dataItem.Banker ?? '—'} activeDeals={!props.dataItem?.NoDeals} text="Projects" />
                                        </div>
                                    </td>
                                : <td colSpan={7}>
                                    <PowerStafferEmptyState canCreate={user?.currentUser?.privileges?.["Projects"]?.Add ?? false} />
                                </td>
                        )}
                    />
                    <Column
                        field="OverallCapacity"
                        title={renderTooltipHeader("Overall Capacity")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("OverallCapacity") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.OverallCapacity ? <PowerCapacity capacity={props.dataItem.OverallCapacity} /> : "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="LastWeekHrs"
                        title={renderTooltipHeader("Hrs Spent Last Wk")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("LastWeekHrs") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.LastWeekHrs ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="WeekendHours"
                        title={renderTooltipHeader("Wknd Hrs Spent")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.WeekendHours ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="TotalHrs"
                        title={renderTooltipHeader("Total Hrs")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("TotalHrs") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.TotalHrs ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="EstHoursForNextWeek"
                        title={renderTooltipHeader("Est. Hrs for Nxt Wk")}
                        columnMenu={columnMenu}
                        headerClassName={columnIsFiltered("EstHoursForNextWeek") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="summary-cell k-table-td" >
                                {props.dataItem.EstHoursForNextWeek ?? "—"}
                            </td>
                        )}
                    />
                    <Column
                        field="LastUpdated"
                        title={renderlastUpdatedTooltipHeader("Last Updated")}
                        columnMenu={columnMenu}
                        filter="date"
                        headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                        cell={(props) => (
                            (members?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                            <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                                <span style={{ pointerEvents: 'none' }}>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                            </td>
                        )}
                    />
                </Grid>
            </div>
            {show && (
                <PowerMemberTrackerModal
                    show={show}
                    onHide={onHide}
                    members={selectedMember}
                    user={user}
                />
            )}
        </div>
    );
};

DashboardPuiMemberTracker.propTypes = {
    members: PropTypes.array,
};

export default DashboardPuiMemberTracker;
